import * as _ from 'lodash';
import { cloneElement, Component } from 'react';
import { connect } from 'react-redux';
/*
 * Route-driven state container for a singular Project
 */
import type { ReactElement } from 'react';

import ProjectActions from '../../actions/ProjectActions';
import NavigationStore from '../../redux/navigation';
import ProjectStore from '../../stores/ProjectStore';
import utils, { getProjectBaseURLFromParams } from '../../utils';
import Loader from '../generic/Loader';
import NotFound from '../generic/NotFound';
import type { NavigationState } from '../../redux/navigation/types';
import type { APIError, IAsync, IAsyncStatus, Project } from '../../types';

import { ProjectProvider } from './contexts/ProjectContext';
import DisplayProjectNav from './DisplayProjectNav';
import getLatestRevisionIdFromProject from './shared/helpers/getLatestRevisionIdFromProject';
import ProjectSubnav from './subnav/ProjectSubnav';

type Props = {
  project: IAsync<Project>;
  params: any;
  location: any;
  // from Redux
  setNavState: (newState: Partial<NavigationState>) => void;
};

type State = {
  status: IAsyncStatus;
};

class ProjectContainer extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      status: {
        loading: true,
      },
    };
  }

  componentDidMount() {
    this.unsubscribeProject = (ProjectStore as any).listen(this.onProjectUpdate);
    this.loadProject(this.props);
    // clicked on a badge
    this.onUpdateNavigation();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      (!this.props.project.data && nextProps.project.data) ||
      (this.props.project.data && !nextProps.project.data) ||
      (this.props.project.data &&
        nextProps.project.data &&
        this.props.project.data.locator !== nextProps.project.data.locator)
    ) {
      this.onUpdateNavigation();
    }
    if (nextProps.params.project_id !== this.props.params.project_id) {
      // note this won't reload on branch change --
      // but we don't currently use that branch data either
      this.loadProject(nextProps);
      // clicked on a badge
      this.onUpdateNavigation();
    }
  }

  componentWillUnmount() {
    if (this.unsubscribeProject) {
      this.unsubscribeProject();
    }
    this.props.setNavState({
      navProps: {
        revision: null,
      },
    });
  }

  private unsubscribeProject: () => void = _.noop;

  loadProject = (props: Props) => {
    ProjectActions.load(
      props.params.project_id,
      {
        ref_type: 'branch',
        ref: props.params.reference,
        ...(props.location?.query?.ref ? { badge_ref: props.location.query.ref } : {}),
      },
    );
  };

  onProjectUpdate = projects => {
    const project = projects[utils.nuc(this.props.params.project_id)];
    if (!project) {
      return;
    }

    this.setState({
      status: project.status,
      data: project.data,
    });
    this.onUpdateNavigation(project);
  };

  onUpdateNavigation = () => {
    this.props.setNavState({
      expand: 'project-expanded',
      subnav: DisplayProjectNav,
      navProps: {
        //  Technically we aren't using the CallToAction component, but this gets rid of the border-bottom for us.
        shouldUseCallToAction: true,
      },
    });
  };

  get latestRevisionIdFromProject() {
    return this.props.params.revision_id || getLatestRevisionIdFromProject(this.props.project);
  }

  get baseProjectURL() {
    const { params } = this.props;
    return `/projects/${utils.nuc(params.project_id)}`;
  }

  get baseRevisionURL() {
    const { project, params } = this.props;
    //  If the project is still loading, just return the base project URL.
    if (project?.status?.loading) {
      return this.baseProjectURL;
    }
    //  If there is no revision, we just assume that we are on the default branch.
    const revisionId = this.latestRevisionIdFromProject;
    if (!params?.revision_id && revisionId) {
      return getProjectBaseURLFromParams({
        project_id: params.project_id,
        reference_type: 'branch',
        reference: project.data?.default_branch ?? 'master',
        revision_id: revisionId,
      });
    }

    return getProjectBaseURLFromParams(params);
  }

  render() {
    const { project } = this.props;

    if ((this.state.status?.error as (APIError & { httpStatusCode?: number }))?.httpStatusCode === 404) {
      return <NotFound />;
    }
    if (project.status.error) {
      const err = _.isString(project.status.error)
        ? project.status.error
        : project.status.error.message;
      if (_.isString(err) && err.toLowerCase().includes('not found')) {
        return <NotFound />;
      }
    }

    return (
      <ProjectProvider value={project}>
        <ProjectSubnav
          projectOrgId={this.props.project.data?.organizationId ?? 0}
          baseProjectURL={this.baseProjectURL}
          baseRevisionURL={this.baseRevisionURL}
          projectLocator={this.props.params.project_id}
          revisionId={this.latestRevisionIdFromProject}
        />
        {project.status.loading && (!project.data || _.isEmpty(project.data)) ? (
          <div className="project-container container margin-top">
            <div className="col-sm-8">
              <div className="panel padded">
                <Loader />
              </div>
            </div>
            <div className="col-sm-4">
              <Loader />
            </div>
          </div>
        ) : (
          <div className="project-container">
            {this.props.children &&
              cloneElement((this.props.children as ReactElement), {
                project,
                onProjectChange: () => this.loadProject(this.props),
                params: this.props.params,
                location: this.props.location,
              })}
          </div>
        )}
      </ProjectProvider>
    );
  }
}

const mapDispatchToProps = {
  setNavState: NavigationStore.actions.setNavState,
};

export default connect(null, mapDispatchToProps)(ProjectContainer);
